import React, { useState, useEffect } from "react";
import { HiChevronLeft as HiChevronLeftSolid } from "react-icons/hi";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { HiPause, HiTrash } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Header from "../../components/layout/Header";
import {
  getAuth,
  GoogleAuthProvider,
  EmailAuthProvider,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  deleteUser,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../../firebase/config";
import { userService } from "../../services/userService";
import { notificationService } from "../../services/notificationService";
import { UserData } from "../../types/user";
import ConfirmDialog from "../../components/ConfirmDialog";
import LoaderBackdrop from "../../components/LoaderBackdrop";
import Toast from "../../components/Toast";
import { useSubscription } from "../../providers/SubscriptionProvider";
import { StripeService } from "../../services/stripeService";
import TwoFaSettings from "../../components/TwoFaSettings";

interface ActiveSession {
  deviceId: string;
  deviceName: string;
  platform: string;
  lastActive: Date;
}

const ACTIVE_SESSION_WINDOW_DAYS = 30;

type ToastType = "success" | "error" | "warning" | "info";

const DELETE_REASONS = [
  "I no longer need the app",
  "I'm switching to a different service",
  "I found the app too expensive",
  "I had privacy/data concerns",
  "I encountered technical issues",
  "Other",
];

const Security: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, sendPasswordReset, logout } = useAuth();
  const { hasSubscription, subscription } = useSubscription();
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);

  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRequireInput, setConfirmRequireInput] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<{
    title: string;
    message: string;
    onConfirm: (inputValue?: string) => Promise<void>;
  }>({
    title: "",
    message: "",
    onConfirm: async () => {},
  });
  const [toast, setToast] = useState<{
    open: boolean;
    type: ToastType;
    message: string;
  }>({
    open: false,
    type: "success",
    message: "",
  });

  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState(DELETE_REASONS[0]);
  const [customReason, setCustomReason] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;
      try {
        const { userData } = await userService.getUserProfile(currentUser.uid);
        setUserData(userData);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchData();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const ref = doc(db, "users", currentUser.uid, "private", "data");
    const unsubscribe = onSnapshot(ref, (snap) => {
      const raw = (snap.data()?.activeSessions ?? {}) as Record<
        string,
        { deviceName?: string; platform?: string; lastActive?: { toDate: () => Date } }
      >;
      const cutoff = DateTime.now().minus({ days: ACTIVE_SESSION_WINDOW_DAYS });

      const sessions = Object.entries(raw)
        .map(([deviceId, session]) => ({
          deviceId,
          deviceName: session.deviceName ?? "Unknown device",
          platform: session.platform ?? "web",
          lastActive: session.lastActive?.toDate() ?? new Date(0),
        }))
        .filter((session) => DateTime.fromJSDate(session.lastActive) > cutoff)
        .sort((a, b) => b.lastActive.getTime() - a.lastActive.getTime());

      setActiveSessions(sessions);
    });

    return unsubscribe;
  }, [currentUser?.uid]);

  const showToast = (type: ToastType, message: string) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 3000);
  };

  const cancelStripeSubscriptionIfExists = async (): Promise<boolean> => {
    const subscriptionId = subscription?.stripeDetails?.subscriptionId;
    if (!hasSubscription || !subscriptionId) return true;

    const cancelled = await StripeService.cancelSubscription(
      subscriptionId,
      currentUser?.uid ?? null,
    );

    if (!cancelled) {
      showToast(
        "error",
        "Failed to cancel your Stripe subscription. Please cancel it manually from Settings > Subscription before proceeding.",
      );
      return false;
    }

    return true;
  };

  const handlePasswordReset = async () => {
    if (!currentUser?.email) return;
    try {
      setIsSending(true);
      setError(null);
      setMessage(null);
      await sendPasswordReset(currentUser.email);
      setMessage("✅ Password reset email has been sent. Check your inbox.");
    } catch (err: any) {
      console.error("Password reset error:", err);
      setError(err.message || "Failed to send password reset email.");
    } finally {
      setIsSending(false);
    }
  };

  const handleToggleAccountStatus = async () => {
    if (!currentUser?.uid) return;
    const isDeactivated = !!userData?.deActivated;

    try {
      setLoading(true);

      if (!isDeactivated) {
        const stripeOk = await cancelStripeSubscriptionIfExists();
        if (!stripeOk) return;
      }

      if (isDeactivated) {
        await userService.activateUser(currentUser.uid);
      } else {
        await userService.deactivateUser(currentUser.uid);
      }

      setUserData((prev: any) =>
        prev ? { ...prev, deActivated: !isDeactivated } : prev,
      );

      showToast(
        "success",
        isDeactivated
          ? "Your account has been reactivated."
          : "Your account has been deactivated and your subscription has been cancelled.",
      );

      if (!isDeactivated) {
        setTimeout(() => (window.location.href = "/account-deactivated"), 700);
      }
    } catch (err: any) {
      console.error("Error updating account status:", err);
      showToast("error", "Failed to update account status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (password?: string) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const reason = selectedReason === "Other" ? customReason.trim() : selectedReason;

    try {
      setLoading(true);

      const stripeOk = await cancelStripeSubscriptionIfExists();
      if (!stripeOk) return;

      const providerId = user.providerData[0]?.providerId;
      if (providerId === "google.com") {
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(user, provider);
      } else if (providerId === "password") {
        if (!password) throw new Error("Password required for reauthentication.");
        const credential = EmailAuthProvider.credential(user.email!, password);
        await reauthenticateWithCredential(user, credential);
      } else {
        throw new Error("Unsupported login provider. Please re-login and try again.");
      }

      await userService.requestAccountDeletion(reason || undefined);
      showToast("success", "Check your email to confirm this request. You will be logged out.");
      setTimeout(() => logout().then(() => (window.location.href = "/")), 800);

    } catch (err: any) {
      console.error("Error requesting deletion:", err);
      showToast("error", `❌ ${err?.message || "Failed to request account deletion."}`);
    } finally {
      setLoading(false);
    }
  };

  const isGoogleUser = currentUser?.providerData?.[0]?.providerId === "google.com";
  const isDeactivated = !!userData?.deActivated;

  const subscriptionWarning = hasSubscription
    ? "\n\nYou have an active subscription. It will be cancelled immediately before proceeding."
    : "";

  const openDeleteReasonDialog = () => {
    setSelectedReason(DELETE_REASONS[0]);
    setCustomReason("");
    setShowReasonDialog(true);
  };

  const proceedToDeleteConfirm = () => {
    if (selectedReason === "Other" && !customReason.trim()) {
      showToast("warning", "Please provide a reason before continuing.");
      return;
    }
    setShowReasonDialog(false);
    const providerId = currentUser?.providerData?.[0]?.providerId || "";
    setConfirmConfig({
      title: "Delete Account",
      message: `Are you sure you want to permanently delete your account? This action cannot be undone.${subscriptionWarning}`,
      onConfirm: async (inputValue?: string) => {
        await handleDeleteAccount(inputValue);
      },
    });
    setConfirmRequireInput(providerId === "password");
    setConfirmOpen(true);
  };

  return (
    <div className="w-full pt-16">
      <Header />
      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
      <ConfirmDialog
        open={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        loading={loading}
        requireInput={confirmRequireInput}
        onConfirm={async (inputValue?: string) => {
          setConfirmOpen(false);
          setLoading(true);
          try {
            await confirmConfig.onConfirm(inputValue);
          } finally {
            setLoading(false);
          }
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <LoaderBackdrop open={loading} />

      <div className="container max-w-2xl mx-auto py-8 px-8 flex flex-col">
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            >
              <HiChevronLeftSolid className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content ml-4">
              Security Settings
            </h1>
          </div>
          <button
            onClick={() => setShowHelpDialog(true)}
            className="p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Information about Security Settings"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-base-100 rounded-lg p-6">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-base-content mb-2">
                Password Management
              </h3>
              <p className="text-sm text-base-content/70">
                Manage your password and account security settings.
              </p>
            </div>
            {isGoogleUser ? (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md text-yellow-700 dark:text-yellow-300 text-sm">
                Password reset is not available for Google-authenticated users.
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <button
                  onClick={handlePasswordReset}
                  disabled={isSending}
                  className={`bg-base-300 w-full text-base-content font-grotesk px-4 py-2 rounded-md hover:opacity-80 shadow-none transition-opacity ${isSending ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {isSending ? "Sending..." : "Send Password Reset Email"}
                </button>
                {message && (
                  <div className="text-sm text-green-700 dark:text-green-400 bg-green-500/10 border border-green-500/30 rounded-md p-3">
                    {message}
                  </div>
                )}
                {error && (
                  <div className="text-sm text-red-700 dark:text-red-400 bg-red-500/10 border border-red-500/30 rounded-md p-3">
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-base-100 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-bold text-base-content">Active Devices</h3>
              {activeSessions.length > 1 && (
                <span className="text-xs font-semibold bg-primary/10 text-primary rounded-full px-2.5 py-1">
                  {activeSessions.length} devices
                </span>
              )}
            </div>
            <p className="text-sm text-base-content/70 mb-4">
              Devices where you're currently signed in.
            </p>
            <div className="space-y-2">
              {activeSessions.length === 0 ? (
                <p className="text-sm text-base-content/50">No active devices found.</p>
              ) : (
                activeSessions.map((session) => (
                  <div
                    key={session.deviceId}
                    className="flex items-center justify-between rounded-md bg-base-200 px-3.5 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-base-content">
                        {session.deviceName}
                        {session.deviceId === notificationService.getCurrentDeviceId() && (
                          <span className="ml-2 text-xs text-base-content/50">(this device)</span>
                        )}
                      </p>
                      <p className="text-xs text-base-content/50">
                        Last active {DateTime.fromJSDate(session.lastActive).toRelative()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-base-100 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <HiPause className={`w-5 h-5 ${isDeactivated ? "text-green-600" : "text-yellow-600"}`} />
                  <h3 className="text-lg font-bold text-base-content">
                    {isDeactivated ? "Activate Account" : "Deactivate Account"}
                  </h3>
                </div>
                <p className="text-sm text-base-content/70">
                  {isDeactivated
                    ? "Reactivate your account to restore access."
                    : "Temporarily disable your account. Your data will remain but no one will be able to connect to your account via the coaches portal, social feed and wearable data syncing will pause."}
                </p>
                {!isDeactivated && hasSubscription && (
                  <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md text-yellow-700 dark:text-yellow-300 text-sm">
                    ⚠️ You have an active subscription. Deactivating your account will <strong>immediately cancel</strong> your subscription.
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  setConfirmConfig({
                    title: isDeactivated ? "Reactivate Account" : "Deactivate Account",
                    message: isDeactivated
                      ? "Do you want to reactivate your account?"
                      : `Are you sure you want to temporarily deactivate your account?${subscriptionWarning}`,
                    onConfirm: async () => {
                      await handleToggleAccountStatus();
                    },
                  });
                  setConfirmRequireInput(false);
                  setConfirmOpen(true);
                }}
                className="w-full font-grotesk px-4 py-2 rounded-md shadow-none bg-base-300 text-base-content hover:opacity-80 transition-opacity"
              >
                {isDeactivated ? "Reactivate Account" : "Deactivate Account"}
              </button>
            </div>
          </div>

          <div className="bg-base-100 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-base-content">Delete Account</h3>
                </div>
                <p className="text-sm text-base-content/70">
                  Permanently remove your account and all associated data.
                </p>
                {hasSubscription && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-700 dark:text-red-400 text-sm">
                    ⚠️ You have an active subscription. Deleting your account will <strong>immediately cancel</strong> your subscription with no refund.
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-red-700 dark:text-red-400">
                <strong>Warning:</strong> This action cannot be undone. All your data, including training sessions, wellness data, and settings will be permanently deleted.
              </p>
              <button
                onClick={openDeleteReasonDialog}
                className="w-full bg-base-300 text-base-content font-grotesk px-4 py-2 rounded-md shadow-none hover:opacity-80 transition-opacity"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        <TwoFaSettings />

        {showReasonDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-grotesk font-semibold text-base-content">
                    Why are you leaving?
                  </h3>
                  <button
                    onClick={() => setShowReasonDialog(false)}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mb-4">
                  Help us improve by letting us know why you're deleting your account.
                </p>
                <div className="space-y-2">
                  {DELETE_REASONS.map((reason) => (
                    <label
                      key={reason}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-base-200 cursor-pointer text-sm text-base-content"
                    >
                      <input
                        type="radio"
                        name="delete-reason"
                        value={reason}
                        checked={selectedReason === reason}
                        onChange={() => setSelectedReason(reason)}
                      />
                      {reason}
                    </label>
                  ))}
                </div>
                {selectedReason === "Other" && (
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Please specify..."
                    className="mt-3 w-full border border-base-300 rounded-md p-2 text-sm bg-base-100 text-base-content"
                    rows={3}
                  />
                )}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowReasonDialog(false)}
                    className="flex-1 bg-base-200 text-base-content font-grotesk px-4 py-2 rounded-md hover:opacity-80 transition-opacity"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={proceedToDeleteConfirm}
                    className="flex-1 bg-red-600 text-white font-grotesk px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showHelpDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-grotesk font-semibold text-base-content">
                    Security Settings
                  </h3>
                  <button
                    onClick={() => setShowHelpDialog(false)}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>
                <div className="space-y-6 text-base-content/80">
                  <div>
                    <h4 className="font-semibold text-base-content mb-2">Password Management</h4>
                    <p className="text-sm">Keep your account secure by managing your password settings:</p>
                    <ol className="mt-2 space-y-1 text-sm list-disc list-inside ml-2">
                      <li>Send password reset emails to your registered email address</li>
                      <li>Update your password regularly for better security</li>
                    </ol>
                    <p className="text-sm mt-4 text-base-content/60">
                      <strong>Note:</strong> If you signed in with Google, password management is handled through your Google account settings.
                    </p>
                  </div>
                  <div className="pt-4 border-t border-base-300">
                    <h4 className="font-semibold text-base-content mb-2">Account Management</h4>
                    <p className="text-sm mb-2">Manage your account status and data:</p>
                    <p className="text-sm mb-2"><strong>Deactivate Account:</strong> Temporarily disable your account. You can reactivate it anytime.</p>
                    <p className="text-sm mb-2"><strong>Delete Account:</strong> Permanently remove your account and all data. This action cannot be undone.</p>
                  </div>
                  <div className="pt-4 -mx-8 px-8">
                    <button
                      onClick={() => setShowHelpDialog(false)}
                      className="w-full bg-neutral text-white font-grotesk px-4 py-2 rounded-md hover:bg-neutral/90 transition-colors"
                    >
                      Okay
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Security;